import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ImageIcon, Loader2, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useFormContext, useFormState } from 'react-hook-form';

import InputControl from '@/components/form/input-control';
import InputTextAreaControl from '@/components/form/input-text-area-control';
import SelectControl from '@/components/form/select-control';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { WithStateQuery } from '@/components/ui/with-state';
import { useConfirmation } from '@/hooks/use-confirm-dialog';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import { fileMutations, fileQueries } from '@/queries/file/file.queries';
import { masterQueries } from '@/queries/master/master.queries';
import { vendorMutations } from '@/queries/vendor/vendor.queries';
import { noOp } from '@/services/fn';
import {
	extractFileParts,
	formatNpwp,
	onFileRejected
} from '@/services/string';
import { useDialogStore } from '@/store';
import type { MainSchema } from '@/types/form/account-manager/form';

import { useTabManager } from './am-content';

function PreviewFile({
	fileKey,
	isDirectUrl = false
}: Readonly<{ fileKey: string, isDirectUrl?: boolean }>) {
	const file = useQuery({
		...fileQueries.getPresign({ key: fileKey }),
		enabled: !isDirectUrl
	});

	if (isDirectUrl) {
		return (
			<img
				src={fileKey}
				alt="NPWP Preview"
				className="max-h-full max-w-full rounded-md object-contain shadow-sm"
			/>
		);
	}
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => (
				<img
					src={url}
					alt="NPWP Preview"
					className="max-h-full max-w-full rounded-md object-contain shadow-sm"
				/>
			)}
		</WithStateQuery>
	);
}

function FileUpload({
	vendorName,
	image,
	isDisabled
}: Readonly<{
	vendorName: string,
	image: string | null,
	isDisabled: boolean
}>) {
	const {
		setValue,
		getValues,
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const { isDirty, errors } = useFormState<MainSchema>({
		name: 'npwp_file_name'
	});

	const [file, setFile] = useState(getValues('npwp_file_name'));
	const [visible, setVisible] = useState<boolean>(false);
	const [progress, setProgress] = useState<number>(0);
	const [previewUrl, setPreviewUrl] = useState<{
		key: string | null,
		isDirectUrl: boolean
	}>({ key: file, isDirectUrl: false });

	const onOpen = useDialogStore((s) => s.onOpenChange);
	const { askCloseIfDirty } = useConfirmation();
	const { onError } = useResponse();

	const uploadMutation = useMutation(fileMutations.uploadPresign);

	const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
		accept: {
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		},
		disabled: uploadMutation.isPending || disabled || isDisabled,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setPreviewUrl({ key: null, isDirectUrl: false });
			setProgress(0);

			const date = new Date();
			const { fileName, fileExt } = extractFileParts(file.name);
			const fileKey = `${vendorName}/${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${crypto.randomUUID()}/${fileName.slice(0, 50)}.${fileExt}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: (sasUrl) => {
						setPreviewUrl({ key: sasUrl, isDirectUrl: true });
						setFile(fileKey);
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'image' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const handleReset = ({
		reset,
		isCreated
	}: {
		reset?: boolean,
		isCreated?: boolean
	}) => {
		const file = isCreated ? getValues('npwp_file_name') : image;
		setFile(reset ? null : file);
		setPreviewUrl({ key: reset ? null : file, isDirectUrl: false });
		setProgress(0);
		uploadMutation.reset();
	};

	const handleSave = () => {
		setValue('npwp_file_name', file, { shouldDirty: true, shouldValidate: true });
		setVisible(false);
	};

	return (
		<>
			<div className="flex w-fit flex-col gap-2">
				<Label className="text-label-input text-xs">NPWP Perusahaan</Label>
				<Button
					variant={'outline'}
					className={cn('flex h-12 items-start', {
						'h-fit items-center': isDisabled
					})}
					onClick={() => setVisible(true)}
					type="button"
				>
					<img src="/icon/upload.webp" alt="icon-upload" className="size-4" />
					<span className="flex flex-col items-start">
						<p className="text-xs text-black">
							{previewUrl.key ? 'Lihat Dokumen (1)' : 'Unggah Dokumen'}
						</p>
						{isDisabled ? null : (
							<p className="text-secondary text-xs font-light">
								{previewUrl.key
									? 'Lihat atau unggah ulang dokumen'
									: 'Unggah Dokumen: JPG,JPEG, PNG (maks. 5MB)'}
							</p>
						)}
					</span>
				</Button>
				{errors.npwp_file_name?.message ? (
					<p
						data-slot="form-message"
						className={cn('text-destructive -mt-2 text-sm')}
					>
						{errors.npwp_file_name.message}
					</p>
				) : null}
			</div>
			<Dialog open={visible} onOpenChange={setVisible}>
				<DialogContent
					onPointerDownOutside={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
					onEscapeKeyDown={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
				>
					{isDisabled ? null : (
						<DialogTitle className="flex flex-col">
							<span className="text-lg text-black">Unggah Dokumen NPWP</span>
							<span className="text-label-input">
								Silakan unggah Dokumen NPWP Anda
							</span>
						</DialogTitle>
					)}
					<div
						{...getRootProps({
							className: cn(
								'dropzone border-2 border-slate-300 border-dashed h-36 flex flex-col gap-1 justify-center items-center p-8 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
								isDragAccept && 'bg-slate-400/50'
							)
						})}
					>
						<input {...getInputProps()} />

						{uploadMutation.isPending ? (
							<div className="z-10 flex flex-col items-center gap-2">
								<Loader2 className="size-8 animate-spin text-blue-500" />
								<span className="text-sm font-medium">Uploading... {progress}%</span>
							</div>
						) : null}

						{!uploadMutation.isPending && previewUrl.key ? (
							<div className="relative flex h-full w-full items-center justify-center p-2">
								<PreviewFile
									fileKey={previewUrl.key}
									isDirectUrl={previewUrl.isDirectUrl}
								/>
								<Button
									variant={'ghost'}
									size={'icon'}
									onClick={(e) => {
										e.stopPropagation();
										handleReset({ reset: true, isCreated: image === null });
									}}
									className={cn(
										'absolute -top-2 -right-2 rounded-full border bg-white p-1 shadow-md transition hover:bg-gray-100',
										{
											hidden: disabled || isDisabled
										}
									)}
								>
									<X className="size-4 text-gray-500" />
								</Button>
							</div>
						) : (
							/* 3. IDLE STATE */
							!uploadMutation.isPending && (
								<>
									<div
										className="flex w-full cursor-pointer items-center justify-center rounded bg-neutral-50 p-4 transition hover:bg-neutral-100"
										onClick={disabled || isDisabled ? noOp : open}
										role="none"
									>
										<ImageIcon className="size-6 text-gray-400" />
									</div>
									<span className="mt-4 font-medium text-black">
										Unggah Dokumen NPWP
									</span>
									<span className="text-secondary mt-1 text-xs">
										Unggah file JPG, JPEG, PNG (maks. 5MB)
									</span>
								</>
							)
						)}
					</div>
					{isDisabled || disabled ? null : (
						<DialogFooter className="shrink-0 sm:justify-end">
							<Button
								type="button"
								disabled={uploadMutation.isPending}
								onClick={handleSave}
							>
								Simpan
							</Button>
						</DialogFooter>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}

function Company() {
	const {
		formState: { disabled }
	} = useFormContext<MainSchema>();

	const options = useQuery(
		masterQueries.listCompany({
			showAll: true,
			orderBy: 'createdDate',
			orderDirection: 'desc'
		})
	);

	return (
		<SelectControl
			name="company_id"
			label="Mendaftar di Perusahaan"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			isLoading={options.isPending}
			isSearchable
			isDisabled={disabled}
		/>
	);
}

function BusinessArea() {
	const {
		formState: { disabled }
	} = useFormContext<MainSchema>();

	const options = useQuery(
		masterQueries.listBusinessArea({
			showAll: true,
			orderBy: 'createdDate',
			orderDirection: 'desc'
		})
	);

	return (
		<SelectControl
			name="business_area_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Bisnis Area"
			isLoading={options.isPending}
			isSearchable
			isDisabled={disabled}
		/>
	);
}

function Qualification() {
	const {
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const options = useQuery(
		masterQueries.listQualification({
			showAll: true,
			orderBy: 'createdDate',
			orderDirection: 'desc'
		})
	);

	return (
		<SelectControl
			name="qualification_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Kualifikasi"
			isLoading={options.isPending}
			isDisabled={disabled}
		/>
	);
}

function TypeCoorporation() {
	const {
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const options = useQuery(
		masterQueries.listTypeCoorporation({
			showAll: true,
			orderBy: 'createdDate',
			orderDirection: 'desc'
		})
	);

	return (
		<SelectControl
			name="type_cooperations"
			label="Jenis Kerjasama"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			isLoading={options.isPending}
			isMulti
			isDisabled={disabled}
		/>
	);
}

function Province() {
	const {
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const options = useQuery(masterQueries.listProvince({ showAll: true }));

	return (
		<SelectControl
			name="province_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Provinsi"
			isLoading={options.isPending}
			isDisabled={disabled}
		/>
	);
}

function City() {
	const {
		watch,
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const provinceId = watch('province_id');
	const options = useQuery(
		masterQueries.listCity({ showAll: true, provinceId })
	);

	return (
		<SelectControl
			name="city_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Kota"
			isLoading={options.isPending}
			isDisabled={disabled}
		/>
	);
}

function District() {
	const {
		watch,
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const cityId = watch('city_id');
	const options = useQuery(
		masterQueries.listDistrict({ showAll: true, cityId })
	);

	return (
		<SelectControl
			name="district_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Kecamatan"
			isLoading={options.isPending}
			isDisabled={disabled}
		/>
	);
}

function Village() {
	const {
		watch,
		formState: { disabled }
	} = useFormContext<MainSchema>();
	const districtId = watch('district_id');
	const options = useQuery(
		masterQueries.listVillage({ showAll: true, districtId })
	);

	return (
		<SelectControl
			name="village_id"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Kelurahan"
			isLoading={options.isPending}
			isDisabled={disabled}
		/>
	);
}

function Npwp({ isDisabled }: Readonly<{ isDisabled: boolean }>) {
	const { watch } = useFormContext<MainSchema>();

	const npwp = watch('npwp');

	return (
		<InputControl
			name="npwp"
			label="Nomor NPWP"
			placeholder="Masukkan No NPWP"
			isDisabled={isDisabled}
			hint={formatNpwp(npwp)}
		/>
	);
}
export default function FormMain({
	vendorName,
	isDisabled = false
}: Readonly<{
	vendorName: string,
	isDisabled?: boolean
}>) {
	const { registerTab } = useTabManager();

	const {
		formState: { isDirty },
		handleSubmit,
		getValues,
		trigger
	} = useFormContext<MainSchema>();
	const submit = useMutation(vendorMutations.mainData);

	useEffect(() => {
		registerTab('main', {
			isDirty,
			submit: () => {
				submit.mutate(getValues());
			}
		});
	}, [isDirty, handleSubmit, registerTab]);

	useEffect(() => {
		trigger();
	}, [trigger]);

	return (
		<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-3">
			<p className="col-span-full text-xl font-medium">Informasi Perusahaan</p>
			<InputControl name="name" label="Nama Perusahaan" isDisabled />
			<Company />
			<BusinessArea />
			<Qualification />
			<TypeCoorporation />
			<InputControl
				name="website"
				label="Website"
				placeholder="Masukkan alamat website perusahaan"
				isDisabled={isDisabled}
			/>
			<InputControl
				name="phone_number"
				label="Telepon Perusahaan"
				placeholder="Masukkan telepon Perusahaan"
				isDisabled={isDisabled}
			/>

			<p className="border-t-accent-foreground/10 col-span-full mt-3 border-t pt-3 text-xl font-medium">
				Alamat Perusahaan
			</p>
			<InputTextAreaControl
				name="address"
				label="Alamat Perusahaan"
				placeholder="Isi alamat lengkap"
				className="col-span-full"
				rows={4}
				disabled={isDisabled}
			/>
			<Province />
			<City />
			<District />
			<Village />
			<InputControl
				name="postal_code"
				label="Kode Pos"
				type="number"
				placeholder="Masukkan nomor Kode POS"
				isDisabled={isDisabled}
			/>

			<p className="border-t-accent-foreground/10 col-span-full mt-3 border-t pt-3 text-xl font-medium">
				Informasi NPWP
			</p>
			<Npwp isDisabled={isDisabled} />
			<InputControl
				name="npwp_name"
				label="Nama NPWP"
				placeholder="Masukkan Nama NPWP"
				isDisabled={isDisabled}
			/>
			<InputTextAreaControl
				name="npwp_address"
				label="Alamat NPWP"
				placeholder="Masukkan alamat NPWP"
				className="col-span-full"
				rows={4}
				disabled={isDisabled}
			/>

			<FileUpload
				vendorName={vendorName}
				image={getValues('npwp_file_name')}
				isDisabled={isDisabled}
			/>

			<p className="border-t-accent-foreground/10 col-span-full mt-3 border-t pt-3 text-xl font-medium">
				Kontak PIC Perusahaan
			</p>
			<div className="col-span-full grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
				<InputControl
					name="pic_name"
					label="Nama Kontak PIC"
					placeholder="Masukkan Nama Kontak PIC"
					isDisabled={isDisabled}
				/>
				<InputControl
					name="pic_email"
					type="email"
					label="Email Kontak PIC"
					placeholder="Masukkan Email Kontak PIC"
					isDisabled={isDisabled}
				/>
				<InputControl
					name="pic_phone_number"
					label="No HP Kontak PIC"
					placeholder="Masukkan No HP Kontak PIC"
					isDisabled={isDisabled}
				/>
				<InputControl
					name="pic_nik"
					label="No KTP Kontak PIC"
					placeholder="Masukkan No KTP Kontak PIC"
					isDisabled={isDisabled}
				/>
			</div>
		</div>
	);
}
